import { computeSchedule } from "./cpm.js";
import { workingDaysBetween } from "./calendar.js";

/* What a change does, worked out before anybody commits to it.
 *
 * This is the question a schedule exists to answer and the one neither WAPA
 * file can: if this slips a day, what else moves, and does the program
 * finish later. Asked in a meeting, answered in the meeting.
 *
 * The method is unglamorous and exactly right: schedule the plan as it stands,
 * apply the change, schedule it again, and diff the two. No separate
 * what-if engine to drift out of step with the real one.
 */

const slipDays = (from, to) => {
  if (!from || !to || from === to) return 0;
  const early = from < to ? from : to;
  const late = from < to ? to : from;
  const days = workingDaysBetween(early, late) - 1;
  return to > from ? days : -days;
};

/* changes: { [activityId]: { start_date, finish_date, duration_days, ... } } */
export function impactOf(activities = [], links = [], changes = {}) {
  const before = computeSchedule(activities, links);

  const after = computeSchedule(
    activities.map((a) => (changes[a.id] ? { ...a, ...changes[a.id] } : a)),
    links,
  );

  /* A change that introduces a loop cannot be scheduled at all, and saying so
     is the only honest answer. Silently keeping the old dates would let
     somebody build a circular plan without ever being told. */
  if (!after.ok) {
    return { ok: false, cycle: after.cycle, moved: [], summary: null };
  }

  const was = new Map(before.activities.map((a) => [a.id, a]));
  const moved = [];

  for (const now of after.activities) {
    const then = was.get(now.id);
    if (!then) continue;
    const startSlip = slipDays(then.early_start, now.early_start);
    const finishSlip = slipDays(then.early_finish, now.early_finish);
    const floatChange = (now.total_float ?? 0) - (then.total_float ?? 0);
    if (!startSlip && !finishSlip && !floatChange) continue;
    moved.push({
      id: now.id,
      title: now.title,
      /* The activity somebody actually touched, as distinct from everything
         that moved because of it. A person needs to see which is which. */
      edited: Boolean(changes[now.id]),
      from: { start: then.early_start, finish: then.early_finish, float: then.total_float },
      to: { start: now.early_start, finish: now.early_finish, float: now.total_float },
      startSlip,
      finishSlip,
      floatChange,
      becameCritical: !then.is_critical && now.is_critical,
      /* Float going below zero means it is now late against its own logic,
         which is a different and worse thing than merely being critical. */
      wentNegative: (then.total_float ?? 0) >= 0 && (now.total_float ?? 0) < 0,
    });
  }

  const knockOn = moved.filter((m) => !m.edited);

  return {
    ok: true,
    cycle: [],
    moved,
    summary: {
      edited: moved.filter((m) => m.edited).length,
      knockOn: knockOn.length,
      projectFinishWas: before.projectFinish,
      projectFinishNow: after.projectFinish,
      projectSlip: slipDays(before.projectFinish, after.projectFinish),
      becameCritical: moved.filter((m) => m.becameCritical).length,
      wentNegative: moved.filter((m) => m.wentNegative).length,
      worstKnockOn: knockOn.reduce(
        (worst, m) => (Math.abs(m.finishSlip) > Math.abs(worst?.finishSlip ?? 0) ? m : worst),
        null,
      ),
    },
  };
}

/* Said in a sentence, because a table of forty rows does not tell somebody in
 * a meeting whether this matters. */
export function describeImpact(impact) {
  if (!impact) return "";
  if (!impact.ok) {
    return `That would make the logic circular, so the dates cannot be worked out. ${
      impact.cycle.length} activities are in the loop.`;
  }

  const s = impact.summary;
  const bits = [];

  if (s.projectSlip > 0) {
    bits.push(`the program finishes ${s.projectSlip} working day${s.projectSlip === 1 ? "" : "s"} later, on ${s.projectFinishNow}`);
  } else if (s.projectSlip < 0) {
    bits.push(`the program finishes ${-s.projectSlip} working day${s.projectSlip === -1 ? "" : "s"} earlier, on ${s.projectFinishNow}`);
  } else {
    bits.push("the program still finishes on the same day");
  }

  if (s.knockOn) {
    bits.push(`${s.knockOn} other activit${s.knockOn === 1 ? "y" : "ies"} move`);
  } else {
    bits.push("nothing else moves");
  }

  if (s.wentNegative) bits.push(`${s.wentNegative} now have negative float`);
  else if (s.becameCritical) bits.push(`${s.becameCritical} become critical`);

  return `${bits.join(", ")}.`;
}
